import React from 'react';

/**
 * Modal to display matching results to participants
 * Uses same styling as RFMResponseForm for consistency
 */
function MatchingResultModal({ result, onClose }) {
  if (!result) return null;

  const {
    request_id,
    ticker,
    matched,
    reason,
    your_role,
    your_side,
    your_qty,
    your_price,
    counterparty_name,
    counterparty_side,
    execution_price,
    execution_qty,
    processed_at
  } = result;

  const isMatched = matched === true;

  // Format numbers for display
  const formatNumber = (num, decimals = 8) => {
    if (num === null || num === undefined) return '-';
    const parsed = parseFloat(num);
    if (isNaN(parsed)) return '-';
    return parsed.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: decimals
    });
  };

  const formatPrice = (num) => formatNumber(num, 2);
  const formatQty = (num) => formatNumber(num, 8);

  // Format timestamp
  const formatTime = (timestamp) => {
    if (!timestamp) return '-';
    return new Date(timestamp).toLocaleString();
  };

  // Determine side display
  const getSideDisplay = (side) => {
    if (!side) return '-';
    const s = side.toLowerCase();
    if (s === 'bid' || s === 'buy') return 'BUY';
    if (s === 'offer' || s === 'sell') return 'SELL';
    return side.toUpperCase();
  };

  const getSideColor = (side) => {
    if (!side) return '#999';
    const s = side.toLowerCase();
    if (s === 'bid' || s === 'buy') return '#26a69a';
    if (s === 'offer' || s === 'sell') return '#ef5350';
    return '#999';
  };

  return (
    <div className="settings-modal-overlay" onClick={onClose}>
      <div
        className="settings-modal"
        onClick={(e) => e.stopPropagation()}
        style={{
          maxWidth: '500px',
          border: isMatched ? '2px solid #26a69a' : '2px solid #ef5350'
        }}
      >
        {/* Header - same style as RFMResponseForm */}
        <div className="settings-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <span style={{ fontSize: '32px' }}>
              {isMatched ? '✅' : '❌'}
            </span>
            <div>
              <h3 style={{ margin: 0, color: isMatched ? '#26a69a' : '#ef5350' }}>
                {isMatched ? 'MATCH FOUND!' : 'NO MATCH'}
              </h3>
              <p className="settings-subtitle">
                {ticker?.toUpperCase()} • {your_role === 'initiator' ? 'You initiated' : 'You responded'}
              </p>
            </div>
          </div>
        </div>

        {/* Request ID - same style as RFMResponseForm */}
        <div style={{
          marginBottom: '16px',
          backgroundColor: '#0a0a0a',
          borderRadius: '8px',
          padding: '12px 16px',
          border: '1px solid #2a2a2a'
        }}>
          <span style={{ fontSize: '12px', color: '#999' }}>Request ID: </span>
          <span style={{ fontSize: '12px', color: '#e5e5e5', fontFamily: 'monospace' }}>{request_id}</span>
        </div>

        {/* Your Order Section */}
        <div className="settings-section" style={{
          backgroundColor: '#0a0a0a',
          borderRadius: '8px',
          border: '1px solid #2a2a2a',
          padding: '16px',
          marginBottom: '16px'
        }}>
          <h3 style={{
            fontSize: '12px',
            fontWeight: '600',
            color: '#999',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            margin: '0 0 12px 0'
          }}>
            Your Order
          </h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <div>
              <span style={{ fontSize: '12px', color: '#666', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Side</span>
              <div style={{
                color: getSideColor(your_side),
                fontWeight: '600',
                fontSize: '18px',
                marginTop: '4px'
              }}>
                {getSideDisplay(your_side)}
              </div>
            </div>
            <div>
              <span style={{ fontSize: '12px', color: '#666', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Quantity</span>
              <div style={{ color: '#e5e5e5', fontWeight: '500', fontSize: '16px', marginTop: '4px' }}>
                {formatQty(your_qty)}
              </div>
            </div>
            <div>
              <span style={{ fontSize: '12px', color: '#666', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Price</span>
              <div style={{ color: '#e5e5e5', fontWeight: '500', fontSize: '16px', marginTop: '4px' }}>
                ${formatPrice(your_price)}
              </div>
            </div>
            <div>
              <span style={{ fontSize: '12px', color: '#666', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Role</span>
              <div style={{ color: '#e5e5e5', fontWeight: '500', fontSize: '16px', marginTop: '4px', textTransform: 'capitalize' }}>
                {your_role || '-'}
              </div>
            </div>
          </div>
        </div>

        {/* Match Details Section (only if matched) */}
        {isMatched && (
          <>
            {/* Counterparty Section */}
            <div className="settings-section" style={{
              backgroundColor: '#0d1b1b',
              borderRadius: '8px',
              border: '1px solid rgba(38, 166, 154, 0.3)',
              padding: '16px',
              marginBottom: '16px'
            }}>
              <h3 style={{
                fontSize: '12px',
                fontWeight: '600',
                color: '#26a69a',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                margin: '0 0 12px 0'
              }}>
                Counterparty
              </h3>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div>
                  <span style={{ fontSize: '12px', color: '#666', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Name</span>
                  <div style={{ color: '#26a69a', fontWeight: '600', fontSize: '18px', marginTop: '4px' }}>
                    {counterparty_name || 'Anonymous'}
                  </div>
                </div>
                <div>
                  <span style={{ fontSize: '12px', color: '#666', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Side</span>
                  <div style={{
                    color: getSideColor(counterparty_side),
                    fontWeight: '600',
                    fontSize: '18px',
                    marginTop: '4px'
                  }}>
                    {getSideDisplay(counterparty_side)}
                  </div>
                </div>
              </div>
            </div>

            {/* Execution Details */}
            <div className="settings-section" style={{
              backgroundColor: '#0d1b1b',
              borderRadius: '8px',
              border: '1px solid rgba(38, 166, 154, 0.5)',
              padding: '16px',
              marginBottom: '16px'
            }}>
              <h3 style={{
                fontSize: '12px',
                fontWeight: '600',
                color: '#26a69a',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                margin: '0 0 12px 0'
              }}>
                Execution Details
              </h3>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div>
                  <span style={{ fontSize: '12px', color: '#666', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Execution Price</span>
                  <div style={{ color: '#26a69a', fontWeight: '700', fontSize: '24px', marginTop: '4px' }}>
                    ${formatPrice(execution_price)}
                  </div>
                </div>
                <div>
                  <span style={{ fontSize: '12px', color: '#666', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Execution Qty</span>
                  <div style={{ color: '#26a69a', fontWeight: '700', fontSize: '24px', marginTop: '4px' }}>
                    {formatQty(execution_qty)}
                  </div>
                </div>
              </div>
            </div>
          </>
        )}

        {/* No Match Reason */}
        {!isMatched && (
          <div className="settings-section" style={{
            backgroundColor: '#1b0d0d',
            borderRadius: '8px',
            border: '1px solid rgba(239, 83, 80, 0.3)',
            padding: '16px',
            marginBottom: '16px'
          }}>
            <h3 style={{
              fontSize: '12px',
              fontWeight: '600',
              color: '#ef5350',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              margin: '0 0 8px 0'
            }}>
              Reason
            </h3>
            <div style={{ color: '#e5e5e5', fontSize: '14px' }}>
              {reason || 'No matching counter-party found for your order.'}
            </div>
          </div>
        )}

        {/* Footer with timestamp */}
        <div style={{
          backgroundColor: '#0a0a0a',
          borderRadius: '8px',
          padding: '12px 16px',
          border: '1px solid #2a2a2a',
          marginBottom: '16px'
        }}>
          <span style={{ fontSize: '12px', color: '#666' }}>Processed: </span>
          <span style={{ fontSize: '12px', color: '#999' }}>{formatTime(processed_at)}</span>
        </div>

        {/* Action Button */}
        <button
          onClick={onClose}
          style={{
            width: '100%',
            padding: '14px 24px',
            backgroundColor: isMatched ? '#26a69a' : '#333',
            color: '#fff',
            border: 'none',
            borderRadius: '8px',
            fontSize: '14px',
            fontWeight: '600',
            cursor: 'pointer',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            transition: 'background-color 0.2s'
          }}
          onMouseEnter={(e) => e.target.style.backgroundColor = isMatched ? '#229688' : '#444'}
          onMouseLeave={(e) => e.target.style.backgroundColor = isMatched ? '#26a69a' : '#333'}
        >
          {isMatched ? 'Acknowledge' : 'Dismiss'}
        </button>
      </div>
    </div>
  );
}

export default MatchingResultModal;
